// 柄（2026-10-21 に app.js から分けた。2026-10-23 に中身へ。柄の一覧の見本は画面の側 palette.ts の patternSwatch）
import { C } from './model.js';
import { conf } from '../config.js';
import { el, keyOf } from './util.js';
import type { Category } from '../types.js';
import type { PatternItem } from './model.js';
import type { PatternLineOptions } from '../../../../../common/js/charat/charat.draw.js';

// ---- 柄（エンジンと同じ charat.draw.js の copyPattern・patternLayer で付ける。CHOCO のデータには柄が無いので、付けられるカテゴリはここで決める） ----
// 柄の SVG は /common/img/switch/pattern_chibi.svg（storm などと同じ 38 柄。1.1MB あるので、柄の一覧を開いたときか柄の付いたキャラを読んだときに読む）。
// 柄を付けられるカテゴリは服と小物（CHOCO のデータには柄の可否 CMODE が無いので、storm で柄が付くもの（CMODE[1] が 1）に合わせて決めた。髪はグラデーションなので付かない）。
// 付け方: 50 四方の <pattern>（柄の座標を -94.5 ずらす）を、基本色用 cpattern_{先頭レイヤー}・影色用 cpatterndk_{先頭レイヤー} の2つ作り、
// パーツの基本色（COLOR[0]）・影色（COLOR[1]）の所に貼る。柄の #3f3f3f の所（と線の #666666）がパーツの色、影色用はその影色で、白い所も影の白に。
// 色を選んでいなければパーツの元の色（エンジンは柄の灰色のまま。ここは新しい画面の決め方で、地の色・影の色を渡している）。
// <pattern> はキャラの SVG の先頭の defs に置く（広場の avatar.js も、完成の画像も、この defs ごと写す）
export const PATTERN_URL = '/common/img/switch/pattern_chibi.svg';
export const PATTERN_KEYS = conf('patternKeys', ['tops', 'outer', 'onep', 'bottoms', 'socks', 'shoes', 'hat', 'hat2', 'glass', 'mask', 'tie', 'wing', 'wing2', 'neck', 'belt', 'glove', 'glove2', 'mantle', 'bag']);
export const PTN_SIZE = 50, PTN_ADJUST = -94.5; // chibi.SIZE_PATTERN・ADJUST_PATTERN
// 色の入れかえで入れかえる2色と線の2色（chibi.COLOR_EX1・EX2・COLOR_STROKE1・2）
export const PTN_EX1 = '#3f3f3f', PTN_EX2 = '#ffffff', PTN_STROKE1 = '#666666', PTN_STROKE2 = '#c7d9e8';
// 柄の描き方の設定（エンジンの chibiConfig と同じ名前・同じ意味。CHOCO のクラシック版 legacy.php にも同じ値）:
//   PATTERN_PARTS_COLOR 色を選んでいない柄の地はパーツの元の色に、PATTERN_REV_PARTS 色の入れかえは柄だけ（パーツそのものの色は入れかえない）
export const PATTERN_PARTS_COLOR = conf('patternPartsColor', true), PATTERN_REV_PARTS = conf('patternRevParts', false);
// 柄を付けられるか: データに柄の可否（CMODE。[1] が 0 でなければ付けられる）があればそれで（クラシック版のエンジンと同じ。GENESIS など、2026-10-22〜）、
// 無ければ PATTERN_KEYS（CHOCO・DRESSUP・Rouge・Blanc のデータには CMODE が無い）。どちらも基本色と影色のあるカテゴリだけ
export function canPattern(cat: Category) {
  if (cat.COLOR.length < 2) return false;
  return cat.CMODE ? cat.CMODE[1] !== 0 : PATTERN_KEYS.indexOf(keyOf(cat)) >= 0;
}
export function loadPatterns(): Promise<PatternItem[]> {
  if (C.patterns) return Promise.resolve(C.patterns);
  if (!C.patternsLoading) {
    // 柄の SVG: キャラのデータに柄（PATTERN）があればそれ（メーカーごとの柄の並び。保存データの柄の番号はこの並びを指す。GENESIS は 40 柄）、
    // 無ければ共通の 38 柄（PATTERN_URL）を後から読む
    const source = C.data && typeof C.data.PATTERN === 'string' && C.data.PATTERN ? Promise.resolve(C.data.PATTERN) : fetch(PATTERN_URL).then((res) => res.text());
    C.patternsLoading = source.then((text) => {
      const root = new DOMParser().parseFromString(text, 'text/html').querySelector('svg');
      const list: PatternItem[] = [], seen: Record<string, boolean> = {};
      Array.prototype.forEach.call(root ? root.children : [], (g: Element) => {
        if (g.tagName.toLowerCase() !== 'g' || !g.id) return;
        const id = g.id.substring(1, 4); // p001・p003_1_ → 001・003（chibi.setCategory と同じ）
        if (seen[id]) return; // 同じ番号の2つ目（p004_3_・p019_1_）は、エンジンでも1つ目が使われる
        seen[id] = true;
        g.id = id;
        list.push({ id: id, g: g });
      });
      C.patterns = list;
      return list;
    }, (e) => { C.patternsLoading = null; throw e; });
  }
  return C.patternsLoading;
}
export function findPattern(id: string) {
  if (!C.patterns || !id) return null;
  return C.patterns.filter((p) => p.id === id)[0] || null;
}
export function patternDefs() {
  let defs = C.svg.querySelector('#ptnDefs');
  if (!defs) {
    defs = el('defs', { id: 'ptnDefs' });
    C.svg.insertBefore(defs, C.svg.firstChild);
  }
  return defs;
}
export function removePatternDefs(cat: Category) {
  const defs = C.svg.querySelector('#ptnDefs');
  if (!defs) return;
  ['cpattern_', 'cpatterndk_'].forEach((head) => {
    const n = defs.querySelector('[id="' + head + cat.LAYER[0] + '"]');
    if (n) n.remove();
  });
}

// ---- クラシック版の柄（C.classicPattern、段階 B4。2026-10-24〜） ----
// 描き方はエンジンの setPartsColorPattern と同じ（charat.draw.js の patternLine に渡すもの）
export function classicPatternOptions(cat: Category): PatternLineOptions {
  const cp = C.classicPattern!;
  return {
    defs: cp.defs(cat), list: cp.list, size: PTN_SIZE, adjust: PTN_ADJUST,
    ex1: PTN_EX1, ex2: PTN_EX2, stroke1: PTN_STROKE1, stroke2: PTN_STROKE2, partsColor: cp.partsColor, revParts: cp.revParts
  };
}
// 柄の番号が一覧にあるか（あれば柄は貼られる。エンジンはそのときカテゴリの全レイヤーに柄の値を入れる。番号はエンジンと同じく == で比べる）
export function classicPatternFound(id: string) {
  const cp = C.classicPattern;
  return !!(cp && cp.list && id) && cp.list.some((g) => g.id == id);
}
